// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
} from "@insite/client-framework/HandlerCreator";
import { sendProductDetailViewEvent } from "@insite/client-framework/OdpEvents/ProductDetailViewEvent";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import prepareProduct from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/PrepareProduct";
import { getSettingsCollection, getShouldLoadCatalog } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import loadProductPricingAndInventory from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = {
    styledOption?: string;
    productId: string;
};
type Props = {
    product?: ProductModel;
    productInfosById?: SafeDictionary<ProductInfo>;
    variantChildren?: ProductModel[];
    variantChildrenProductInfos?: ProductInfo[];
    variantSelection?: SafeDictionary<string>;
    pricingLoaded?: true;
    inventoryLoaded?: true;
};

type HandlerType = Handler<Parameter, Props>;

export const DeferCallIfNecessary: HandlerType = props => {
    if (!getShouldLoadCatalog(props.getState())) {
        props.dispatch({
            type: "Context/AddDeferredApiCall",
            deferredCallType: "catalog",
        });
        return false;
    }
};

export const RequestDataFromProductApi: HandlerType = async props => {
    props.dispatch({
        type: "xBargain/isProductLoading",
        id: props.parameter.productId,
    } as any);

    // XNG-Changes: AWCS-448: handle product not found error
    try {
        props.product = await getProductById({
            id: props.parameter.productId,
            expand: [
                "detail",
                "specifications",
                "content",
                "images",
                "documents",
                "attributes",
                "variantTraits",
                "properties",
            ],
            includeAttributes: ["includeOnProduct"],
        });
    } catch {
        props.dispatch({
            type: "xBargain/ProductNotFound",
        } as any);
        return;
    }

    const configuration = props.product?.detail?.configuration;
    if (configuration) {
        props.dispatch({
            type: "Pages/ProductDetails/InitConfigurationSelection",
            configuration,
        });
    }
    // from LoadProductByPath handler, try to store id instead of path
    props.dispatch({
        type: "Data/Products/CompleteLoadProduct",
        model: props.product,
        path: props.product.id,
    });

    props.dispatch({
        type: "xBargain/SetProductId",
        id: props.parameter.productId,
    } as any);
};

export const RequestVariantChildrenFromApi: HandlerType = async props => {
    if (!props.product?.variantTraits || props.product.variantTraits.length === 0) {
        return;
    }

    props.variantChildren = getVariantChildrenDataView(props.getState(), props.product.id).value;
    if (!props.variantChildren) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadVariantChildren,
            { productId: props.product.id },
            props,
        );
    }
};

export const SetupVariantSelection: HandlerType = props => {
    props.variantSelection = getVariantSelection(props.product, props.variantChildren, props.parameter.styledOption);
};

function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren) {
        return variantSelection;
    }

    if (styledOption || product.defaultChildProductId) {
        const variantChild = styledOption
            ? variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase())
            : variantChildren.find(o => o.id === product.defaultChildProductId);

        variantChild?.childTraitValues?.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });

        if (Object.keys(variantSelection).length > 0) {
            return variantSelection;
        }
    }

    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        variantSelection[variantTrait.id] = defaultVariantValue?.id ?? "";
    });

    return variantSelection;
}

export const SetUnitOfMeasureAndQtyOrdered: HandlerType = props => {
    if (!props.product) {
        return;
    }

    const productInfos = [createFromProduct(props.product)];
    if (props.variantChildren) {
        productInfos.push(...props.variantChildren.map(createFromProduct));
    }

    props.productInfosById = productInfos.reduce((map, productInfo) => {
        map[productInfo.productId] = productInfo;
        return map;
    }, {} as SafeDictionary<ProductInfo>);
};

export const DispatchCompleteLoadProduct: HandlerType = props => {
    if (!props.product) {
        return;
    }

    props.dispatch({
        type: "Pages/ProductDetails/CompleteLoadProduct",
        productInfosById: props.productInfosById ?? {},
        variantSelection: props.variantSelection ?? {},
        selectedProductId: Object.keys(props.variantSelection as any).length ? undefined : props.product.id,
        configuration: props.product.detail?.configuration,
    });
};

export const TrackInOdp: HandlerType = props => {
    if (!props.product) {
        return;
    }

    sendProductDetailViewEvent(props.product.id, props.getState());
};

export const LoadRealTimePricingAndInventory: HandlerType = props => {
    if (!props.product) {
        return;
    }

    props.dispatch(
        loadProductPricingAndInventory({
            product: props.product,
            variantChildren: props.variantChildren,
            productInfosById: props.productInfosById,
            styledOption: props.parameter.styledOption,
            waitForDataLoaded:
                !!props.product.variantTypeId &&
                (!getIsWebCrawler() ||
                    getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo),
            onComplete: () => {
                props.pricingLoaded = props.inventoryLoaded = true;
            },
        }),
    );
};

/** @deprecated LoadRealTimePrices is no longer used in this handler and will be removed in a future release. @see LoadRealTimePricingAndInventory instead. */
export const LoadRealTimePrices: HandlerType = props => {};

/** @deprecated LoadRealTimeInventory is no longer used in this handler and will be removed in a future release. @see LoadRealTimePricingAndInventory instead. */
export const LoadRealTimeInventory: HandlerType = props => {};

export const InitVariantProduct: HandlerType = async props => {
    if (!props.product || !props.product.variantTypeId) {
        return;
    }

    if (
        !getIsWebCrawler() ||
        getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo
    ) {
        const checkData = () => {
            return !!props.pricingLoaded && !!props.inventoryLoaded;
        };
        await waitFor(checkData);
    }

    props.dispatch(updateVariantSelection({ productId: props.product.id }));
};

export const PrepareProduct: HandlerType = ({ dispatch, product }) => {
    if (product) {
        dispatch(prepareProduct({ product }));
    }
};

export const chain = [
    DeferCallIfNecessary,
    RequestDataFromProductApi,
    RequestVariantChildrenFromApi,
    SetupVariantSelection,
    SetUnitOfMeasureAndQtyOrdered,
    DispatchCompleteLoadProduct,
    TrackInOdp,
    LoadRealTimePricingAndInventory,
    InitVariantProduct,
    PrepareProduct,
];

const displayProduct = createHandlerChainRunner(chain, "DisplayProduct");
export default displayProduct;
